import type { Resource } from 'i18next';
import i18next from 'i18next';
import { useEffect, useState } from 'react';

import type { CogI18nInstance } from '../types';

import type {
  getLanguage as defaultGetLanguage,
  selectLanguage as defaultSelectLanguage,
} from '../utils';
import { createCogI18nInstance as createCogI18nInstanceImpl } from '../utils/createI18nInstance';
import { overrideFontFamily } from '../utils/overrideFontFamily';

export interface I18nWrapperViewModelResult {
  isLoading: boolean;
  error?: Error;
  i18n?: CogI18nInstance<Record<string, string>>;
}

export type UseI18nWrapperViewModelDependencies = {
  createCogI18nInstance: typeof createCogI18nInstanceImpl;
};

const defaultdependencies: UseI18nWrapperViewModelDependencies = {
  createCogI18nInstance: createCogI18nInstanceImpl,
};

interface I18nWrapperViewModelProps {
  translations: Resource;
  defaultNamespace?: string;
  /**
   * Pre-built Cog i18n instance. When provided, I18nWrapper skips creation
   * entirely and uses it as-is — the caller owns its lifecycle (init,
   * language, resources). Useful for tests/mocking.
   */
  i18n?: CogI18nInstance<Record<string, string>>;
  getLanguage: typeof defaultGetLanguage;
  selectLanguage: typeof defaultSelectLanguage;
  dependencies?: UseI18nWrapperViewModelDependencies;
}
export const useI18nWrapperViewModel = ({
  translations,
  defaultNamespace,
  i18n,
  getLanguage,
  selectLanguage,
  dependencies = defaultdependencies,
}: I18nWrapperViewModelProps): I18nWrapperViewModelResult => {
  const [cogInstance, setCogInstance] = useState<
    CogI18nInstance<Record<string, string>> | undefined
  >(i18n);
  const activeInstance = i18n ?? cogInstance;
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    // A ready-made Cog instance was given — nothing to create.
    if (i18n) return;
    let cancelled = false;
    const language = getLanguage();

    // No explicit instance was given: reuse the shared global i18next
    // singleton, so namespaces from multiple <I18nWrapper>s merge into one
    // shared instance instead of every wrapper creating its own island.
    dependencies
      .createCogI18nInstance(
        {
          language,
          translations,
          defaultNamespace,
          selectLanguage,
        },
        i18next
      )
      .then((created) => {
        if (!cancelled) {
          setCogInstance(created);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      });

    overrideFontFamily(language ?? 'en');

    return () => {
      cancelled = true;
    };
    // Create/reuse once. Language is fetched via getLanguage() before
    // creation, so a non-default starting language is supplied up front.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    i18n: activeInstance,
    error,
    isLoading: !activeInstance,
  };
};
